import { ClipboardList, Download, AlertCircle, AlertTriangle, Info, Loader2 } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useAudit } from '@/hooks/useApi'
import { useState } from 'react'

type EventFilter = 'all' | 'blocked' | 'allowed'

export function AuditPanel() {
  const [limit, setLimit] = useState(50)
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<EventFilter>('all')

  const { data, isLoading, refetch } = useAudit(limit, typeFilter || undefined)
  const events = data?.events || []

  const filtered = events.filter((e) => {
    if (statusFilter === 'blocked' && !e.blocked) return false
    if (statusFilter === 'allowed' && e.blocked) return false
    return true
  })

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            Security Events
            {isLoading && <Loader2 className="h-3 w-3 animate-spin text-text-muted" />}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => refetch()}>
              Refresh
            </Button>
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
              <Download className="h-3 w-3" />
              Export
            </Button>
          </div>
        </div>
        <div className="flex gap-1.5 mt-2 flex-wrap">
          {(['all', 'blocked', 'allowed'] as const).map((s) => (
            <Button
              key={s}
              variant={statusFilter === s ? 'default' : 'outline'}
              size="sm"
              className="h-6 text-[10px] px-2"
              onClick={() => setStatusFilter(s)}
            >
              {s}
            </Button>
          ))}
          <div className="w-px bg-border mx-1" />
          <input
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            placeholder="Filter by event type..."
            className="h-6 text-[10px] px-2 rounded border border-border bg-surface text-text-primary"
          />
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="h-6 text-[10px] px-1 rounded border border-border bg-surface text-text-primary"
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {filtered.map((evt) => {
            const isBlocked = evt.blocked === 1
            const Icon = isBlocked ? AlertCircle : evt.event_type.includes('QUARANTINE') ? AlertTriangle : Info

            let details = ''
            try { details = typeof evt.details === 'string' ? evt.details : JSON.stringify(evt.details) } catch { details = String(evt.details) }

            return (
              <div key={evt.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-surface-elevated/50 transition-colors">
                <Icon className={cn('h-4 w-4 shrink-0', isBlocked ? 'text-danger' : 'text-text-muted')} />
                <span className="text-xs font-mono text-text-muted w-8 shrink-0">#{evt.id}</span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-text-primary block truncate">{evt.event_type}</span>
                  <span className="text-[11px] text-text-muted truncate block">{details.slice(0, 100)}</span>
                </div>
                <span className="text-[10px] text-text-muted shrink-0">{new Date(evt.created_at).toLocaleString()}</span>
                {isBlocked && <Badge variant="danger" className="text-[10px] shrink-0">blocked</Badge>}
              </div>
            )
          })}
          {filtered.length === 0 && (
            <div className="text-center py-8 text-sm text-text-muted">
              {isLoading ? 'Loading events...' : 'No events match the current filters.'}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
